export interface Point {
  x: number;
  y: number;
}

export type Scale = (value: number) => number;

/** Maps a value from `domain` onto `range`. A zero-width domain pins to the middle. */
export function linearScale(
  domain: [number, number],
  range: [number, number],
): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (span === 0) return () => (r0 + r1) / 2;
  return (value) => r0 + ((value - d0) / span) * (r1 - r0);
}

/** Min and max of a sample, or null when there is nothing to measure. */
export function extent(values: number[]): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min === Infinity ? null : [min, max];
}

/**
 * Pad a domain so points do not sit on the frame. A flat domain is opened out to
 * something drawable rather than collapsing to a line at the midpoint.
 */
export function padDomain(
  domain: [number, number],
  fraction = 0.08,
): [number, number] {
  const [min, max] = domain;
  const span = max - min;
  if (span === 0) {
    const nudge = Math.abs(min) * 0.01 || 0.5;
    return [min - nudge, max + nudge];
  }
  const pad = span * fraction;
  return [min - pad, max + pad];
}

/** Round tick values covering `[min, max]`, at roughly `count` intervals. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || count < 1) return [];
  if (min === max) return [min];

  const rawStep = (max - min) / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalised = rawStep / magnitude;
  const stepFactor = normalised >= 5 ? 5 : normalised >= 2 ? 2 : 1;
  const step = stepFactor * magnitude;

  const ticks: number[] = [];
  const first = Math.ceil(min / step) * step;
  // Guard against floating-point drift adding a spurious final tick.
  for (let t = first; t <= max + step * 1e-9; t += step) {
    ticks.push(Number(t.toFixed(6)));
  }
  return ticks;
}

/**
 * Reduce a series to at most `maxPoints` while keeping its shape.
 *
 * Each bucket contributes its minimum and maximum, so spikes survive — which is
 * the point for lap traces, where the outliers (in-laps, traffic) are the
 * interesting part and plain sampling would drop them.
 */
export function downsample(points: Point[], maxPoints: number): Point[] {
  if (maxPoints < 2 || points.length <= maxPoints) return points;

  const bucketCount = Math.floor(maxPoints / 2);
  const bucketSize = points.length / bucketCount;
  const out: Point[] = [];

  for (let b = 0; b < bucketCount; b += 1) {
    const start = Math.floor(b * bucketSize);
    const end = Math.min(points.length, Math.floor((b + 1) * bucketSize));
    if (end <= start) continue;

    let lo = points[start];
    let hi = points[start];
    for (let i = start + 1; i < end; i += 1) {
      if (points[i].y < lo.y) lo = points[i];
      if (points[i].y > hi.y) hi = points[i];
    }

    // Emit in x order so the drawn line does not double back on itself.
    if (lo.x <= hi.x) {
      out.push(lo);
      if (hi !== lo) out.push(hi);
    } else {
      out.push(hi);
      out.push(lo);
    }
  }

  return out;
}

/** An SVG path through the points; empty when there is nothing to draw. */
export function buildPath(points: Point[]): string {
  if (points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
}

/**
 * Colours for overlaid series, as custom properties so they follow the theme.
 * They resolve only through `style`, never an SVG presentation attribute:
 * stroke="var(--x)" does not parse, style={{ stroke: 'var(--x)' }} does.
 */
export const SERIES_COLOURS = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
] as const;

export function seriesColour(index: number): string {
  return SERIES_COLOURS[index % SERIES_COLOURS.length];
}
